(() => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const initialize = (section) => {
    if (!section || section.dataset.reviewsInitialized === "true") return;

    const viewport = section.querySelector("[data-review-viewport]");
    const track = section.querySelector("[data-review-track]");
    const previous = section.querySelector("[data-review-previous]");
    const next = section.querySelector("[data-review-next]");
    const pagination = section.querySelector("[data-review-pagination]");
    const sort = section.querySelector("[data-review-sort]");
    const sortValue = section.querySelector("[data-review-sort-value]");
    const mediaButtons = Array.from(section.querySelectorAll("[data-review-target]"));
    const dialog = section.querySelector("[data-review-dialog]");
    const dialogContent = dialog?.querySelector("[data-review-dialog-content]");
    const dialogCloseButtons = dialog ? Array.from(dialog.querySelectorAll("[data-review-dialog-close]")) : [];
    const reviewFormDialog = section.querySelector("[data-review-form-dialog]");
    const reviewFormOpen = section.querySelector("[data-review-form-open]");
    const reviewFormCloseButtons = reviewFormDialog ? Array.from(reviewFormDialog.querySelectorAll("[data-review-form-close]")) : [];
    const reviewFormSteps = reviewFormDialog ? Array.from(reviewFormDialog.querySelectorAll("[data-review-form-step]")) : [];
    const reviewFormNext = reviewFormDialog?.querySelector("[data-review-form-next]");
    const reviewFormBack = reviewFormDialog?.querySelector("[data-review-form-back]");
    const ratingButtons = reviewFormDialog ? Array.from(reviewFormDialog.querySelectorAll("[data-review-rating-button]")) : [];
    const ratingValue = reviewFormDialog?.querySelector("[data-review-rating-value]");
    const ratingPreview = reviewFormDialog?.querySelector("[data-review-rating-preview]");
    const ratingError = reviewFormDialog?.querySelector("[data-review-rating-error]");
    const reviewContactForm = reviewFormDialog?.querySelector("form");
    let cards = Array.from(section.querySelectorAll("[data-review-card]"));
    const originalOrder = [...cards];
    const autoSlideEnabled = section.dataset.autoSlide === "true";
    const pauseOnHover = section.dataset.pauseAutoSlide === "true";
    const interval = Math.max(Number(section.dataset.autoSlideInterval) || 4500, 2500);

    if (!viewport || !track) return;
    section.dataset.reviewsInitialized = "true";

    let timer = null;
    let lastDialogTrigger = null;
    let paginationButtons = [];

    const scrollStep = () => {
      const firstCard = cards[0];
      const gap = Number.parseFloat(window.getComputedStyle(track).gap) || 0;
      return firstCard ? firstCard.getBoundingClientRect().width + gap : viewport.clientWidth;
    };

    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    const move = (direction) => {
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const step = scrollStep();
      const nextLeft = viewport.scrollLeft + (step * direction);
      const behavior = reducedMotion.matches ? "auto" : "smooth";

      if (nextLeft < 0) {
        viewport.scrollTo({ left: maxScroll, behavior: "auto" });
        window.requestAnimationFrame(() => {
          viewport.scrollTo({ left: Math.max(0, maxScroll - step), behavior });
        });
        return;
      }

      if (nextLeft > maxScroll + 2) {
        viewport.scrollTo({ left: 0, behavior: "auto" });
        window.requestAnimationFrame(() => {
          viewport.scrollTo({ left: Math.min(step, maxScroll), behavior });
        });
        return;
      }

      viewport.scrollTo({ left: nextLeft, behavior });
    };

    const updatePagination = () => {
      if (!paginationButtons.length) return;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const activeIndex = maxScroll > 0
        ? Math.min(paginationButtons.length - 1, Math.round((viewport.scrollLeft / maxScroll) * (paginationButtons.length - 1)))
        : 0;
      paginationButtons.forEach((button, index) => {
        const isActive = index === activeIndex;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-current", isActive ? "true" : "false");
      });
    };

    const renderPagination = () => {
      if (!pagination) return;
      const count = Math.min(cards.length, 8);
      pagination.replaceChildren();
      paginationButtons = Array.from({ length: count }, (_, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "luv-product-reviews__pagination-dot";
        button.setAttribute("aria-label", `Go to review page ${index + 1}`);
        button.addEventListener("click", () => {
          const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
          const left = count > 1 ? maxScroll * (index / (count - 1)) : 0;
          viewport.scrollTo({ left, behavior: reducedMotion.matches ? "auto" : "smooth" });
          restart();
        });
        pagination.appendChild(button);
        return button;
      });
      updatePagination();
    };

    const start = () => {
      if (!autoSlideEnabled || reducedMotion.matches || timer || cards.length < 2 || (dialog && !dialog.hidden) || (reviewFormDialog && !reviewFormDialog.hidden)) return;
      timer = window.setInterval(() => {
        move(1);
      }, interval);
    };

    const restart = () => {
      stop();
      start();
    };

    previous?.addEventListener("click", () => {
      move(-1);
      restart();
    });
    next?.addEventListener("click", () => {
      move(1);
      restart();
    });
    viewport.addEventListener("scroll", updatePagination, { passive: true });
    window.addEventListener("resize", renderPagination);

    sort?.addEventListener("change", () => {
      const mode = sort.value;
      if (sortValue) sortValue.textContent = sort.options[sort.selectedIndex]?.text || "Featured";
      const sorted = [...cards].sort((first, second) => {
        if (mode === "newest") return (second.dataset.date || "").localeCompare(first.dataset.date || "");
        if (mode === "highest") return Number(second.dataset.rating) - Number(first.dataset.rating);
        if (mode === "lowest") return Number(first.dataset.rating) - Number(second.dataset.rating);
        return originalOrder.indexOf(first) - originalOrder.indexOf(second);
      });
      sorted.forEach((card) => track.appendChild(card));
      cards = sorted;
      viewport.scrollTo({ left: 0, behavior: reducedMotion.matches ? "auto" : "smooth" });
      renderPagination();
      restart();
    });

    const closeDialog = () => {
      if (!dialog || dialog.hidden) return;
      dialog.hidden = true;
      dialog.setAttribute("aria-hidden", "true");
      dialogContent?.replaceChildren();
      document.documentElement.classList.remove("luv-review-dialog-open");
      lastDialogTrigger?.focus();
      lastDialogTrigger = null;
      start();
    };

    const openDialog = (card) => {
      const detail = card.querySelector("[data-review-detail]");
      if (!dialog || !dialogContent || !detail) return;
      stop();
      dialogContent.replaceChildren(detail.content.cloneNode(true));
      lastDialogTrigger = card;
      document.documentElement.classList.add("luv-review-dialog-open");
      dialog.hidden = false;
      dialog.setAttribute("aria-hidden", "false");
      dialog.querySelector(".luv-product-reviews__dialog-close")?.focus();
    };

    cards.forEach((card) => {
      card.addEventListener("click", (event) => {
        if (event.target.closest("a, button, select")) return;
        openDialog(card);
      });
      card.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        openDialog(card);
      });
    });

    dialogCloseButtons.forEach((button) => button.addEventListener("click", closeDialog));

    let selectedRating = 0;
    let lastReviewFormTrigger = null;

    const showReviewFormStep = (stepNumber) => {
      reviewFormSteps.forEach((step) => {
        step.hidden = step.dataset.reviewFormStep !== String(stepNumber);
      });
      const activeStep = reviewFormSteps.find((step) => !step.hidden);
      const focusTarget = stepNumber === 2
        ? activeStep?.querySelector("textarea")
        : activeStep?.querySelector("[data-review-rating-button]");
      focusTarget?.focus({ preventScroll: true });
      reviewFormDialog?.querySelector(".luv-product-reviews__form-panel")?.scrollTo({ top: 0, behavior: "auto" });
    };

    const closeReviewForm = () => {
      if (!reviewFormDialog || reviewFormDialog.hidden) return;
      reviewFormDialog.hidden = true;
      reviewFormDialog.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("luv-review-dialog-open");
      lastReviewFormTrigger?.focus();
      lastReviewFormTrigger = null;
      start();
    };

    const openReviewForm = () => {
      if (!reviewFormDialog) return;
      stop();
      lastReviewFormTrigger = reviewFormOpen;
      document.documentElement.classList.add("luv-review-dialog-open");
      reviewFormDialog.hidden = false;
      reviewFormDialog.setAttribute("aria-hidden", "false");
      reviewFormDialog.querySelector("[data-review-form-close]")?.focus();
    };

    reviewFormOpen?.addEventListener("click", openReviewForm);
    reviewFormCloseButtons.forEach((button) => button.addEventListener("click", closeReviewForm));

    const paintRating = (rating) => {
      ratingButtons.forEach((button) => {
        const filled = Number(button.dataset.reviewRatingButton) <= rating;
        button.classList.toggle("is-filled", filled);
        button.setAttribute("aria-pressed", String(Number(button.dataset.reviewRatingButton) === rating));
      });
      if (ratingPreview) {
        ratingPreview.textContent = `${"★".repeat(rating)}${"☆".repeat(5 - rating)}`;
        ratingPreview.setAttribute("aria-label", `${rating} out of 5 stars`);
      }
    };

    ratingButtons.forEach((button) => {
      button.addEventListener("mouseenter", () => paintRating(Number(button.dataset.reviewRatingButton)));
      button.addEventListener("focus", () => paintRating(Number(button.dataset.reviewRatingButton)));
      button.addEventListener("click", () => {
        selectedRating = Number(button.dataset.reviewRatingButton);
        if (ratingValue) ratingValue.value = `${selectedRating} out of 5 stars`;
        if (ratingError) ratingError.hidden = true;
        paintRating(selectedRating);
      });
    });
    reviewFormDialog?.querySelector("[data-review-rating]")?.addEventListener("mouseleave", () => paintRating(selectedRating));

    reviewFormNext?.addEventListener("click", () => {
      if (!selectedRating) {
        if (ratingError) ratingError.hidden = false;
        ratingButtons[0]?.focus();
        return;
      }
      showReviewFormStep(2);
    });

    reviewFormBack?.addEventListener("click", () => showReviewFormStep(1));

    reviewContactForm?.addEventListener("submit", (event) => {
      if (selectedRating) return;
      event.preventDefault();
      if (ratingError) ratingError.hidden = false;
      showReviewFormStep(1);
      ratingButtons[0]?.focus();
    });

    if (reviewFormDialog?.querySelector("[data-review-form-success]") || reviewFormDialog?.querySelector("[data-review-form-errors]:not(.is-hidden)")) {
      window.setTimeout(openReviewForm, 0);
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && dialog && !dialog.hidden) closeDialog();
      if (event.key === "Escape" && reviewFormDialog && !reviewFormDialog.hidden) closeReviewForm();
    });

    mediaButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const card = section.querySelector(`[data-review-id="${CSS.escape(button.dataset.reviewTarget)}"]`);
        if (!card) return;
        card.scrollIntoView({ behavior: reducedMotion.matches ? "auto" : "smooth", block: "nearest", inline: "center" });
        window.setTimeout(() => openDialog(card), reducedMotion.matches ? 0 : 260);
      });
    });

    if (pauseOnHover) {
      section.addEventListener("mouseenter", stop);
      section.addEventListener("mouseleave", start);
      section.addEventListener("focusin", stop);
      section.addEventListener("focusout", (event) => {
        if (!section.contains(event.relatedTarget)) start();
      });
    }

    reducedMotion.addEventListener("change", restart);
    renderPagination();
    start();
  };

  const initializeAll = (root = document) => {
    if (root.matches?.("[data-luv-product-reviews]")) initialize(root);
    root.querySelectorAll?.("[data-luv-product-reviews]").forEach(initialize);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
